import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { ProductCard } from "@/components/site/ProductCard";
import { MaskLine } from "@/components/site/Reveal";
import { TOPIC_LINKS } from "@/lib/topics";
import { useSeo, SITE_URL, absUrl } from "@/lib/seo";
import { COLLECTIONS, COLLECTION_LINKS, collectionProducts } from "@/lib/collections";
import { useCollectionTexts } from "@/hooks/useCollectionTexts";

function Breadcrumb({ name }) {
  return (
    <nav aria-label="Fil d'Ariane" data-testid="collection-breadcrumb" className="mb-6">
      <ol className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--parchment)]/70">
        <li><Link to="/" data-testid="breadcrumb-home" className="hover:text-[var(--gold-soft)]">Accueil</Link></li>
        <li aria-hidden="true"><ChevronRight className="h-3 w-3" /></li>
        <li><Link to="/boutique" data-testid="breadcrumb-shop" className="hover:text-[var(--gold-soft)]">Boutique</Link></li>
        <li aria-hidden="true"><ChevronRight className="h-3 w-3" /></li>
        <li aria-current="page" className="text-[var(--gold)]">{name}</li>
      </ol>
    </nav>
  );
}

const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} aria-pressed={on}
    className={`h-10 shrink-0 whitespace-nowrap rounded-full border px-5 text-xs uppercase tracking-[0.18em] transition-[background-color,color,border-color] duration-300 ${on ? "border-[var(--gold)] bg-[var(--gold)] text-[var(--ink)]" : "border-[var(--line)] text-[var(--parchment)]/85 hover:border-[var(--gold)]"}`}>
    {children}
  </button>
);

function Guide({ c, list }) {
  const groups = list.reduce((m, p) => ({ ...m, [c.needs(p)]: [...(m[c.needs(p)] || []), p] }), {});
  return (
    <section data-testid="collection-guide" className="mt-28 border-t border-[var(--line)] pt-16">
      <h2 className="font-display text-3xl sm:text-4xl">{c.guideTitle}</h2>
      <div className="mt-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
        {Object.entries(groups).map(([need, ps]) => (
          <div key={need}>
            <h3 className="font-display text-xl italic text-[var(--gold-soft)]">{need}</h3>
            <ul className="mt-3 space-y-2 text-sm text-[var(--parchment)]/85">
              {ps.map((p) => <li key={p.slug}><Link to={`/produit/${p.slug}`} className="underline-offset-4 hover:text-[var(--gold-soft)] hover:underline">{p.name}</Link> — {p.tagline}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function OtherRanges({ current }) {
  const links = [...COLLECTION_LINKS.filter((l) => l.to !== current), ...TOPIC_LINKS, { to: "/boutique?type=pack", label: "Packs Ozentya" }, { to: "/conseils", label: "Conseils & routines" }];
  return (
    <section data-testid="collection-other-ranges" className="mt-20">
      <h2 className="eyebrow mb-5">Explorer les autres gammes</h2>
      <div className="flex flex-wrap gap-2">
        {links.map((l) => <Link key={l.to} to={l.to} data-testid={`collection-range-link-${l.label}`} className="flex h-10 items-center rounded-full border border-[var(--line)] px-5 text-xs uppercase tracking-[0.18em] text-[var(--parchment)]/85 transition-colors hover:border-[var(--gold)] hover:text-[var(--gold-soft)]">{l.label}</Link>)}
      </div>
    </section>
  );
}

const ld = (c, list) => ({
  "@context": "https://schema.org",
  "@graph": [
    { "@type": "CollectionPage", name: c.name, url: `${SITE_URL}${c.path}`, description: c.description,
      mainEntity: { "@type": "ItemList", itemListElement: list.map((p, i) => ({ "@type": "ListItem", position: i + 1, url: `${SITE_URL}/produit/${p.slug}`, name: p.name, image: absUrl(p.image) })) } },
    { "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Boutique", item: `${SITE_URL}/boutique` },
      { "@type": "ListItem", position: 3, name: c.name, item: `${SITE_URL}${c.path}` },
    ] },
  ],
});

export default function CollectionPage({ id }) {
  const c = COLLECTIONS[id];
  const { data, isLoading, isError } = useProducts();
  const [need, setNeed] = useState("");
  const texts = useCollectionTexts();
  const intro = texts.data?.[id]?.intro || c.intro;
  const list = useMemo(() => collectionProducts(c, Array.isArray(data) ? data : []), [c, data]);
  const needs = c.needs ? [...new Set(list.map(c.needs))] : [];
  const shown = need ? list.filter((p) => c.needs(p) === need) : list;
  useSeo({ title: c.title, description: c.description, path: c.path, image: list[0]?.image, jsonLd: ld(c, list) });

  return (
    <main data-testid={`collection-page-${id}`} className="mx-auto max-w-[1440px] px-5 pb-32 pt-44 md:px-10">
      <Breadcrumb name={c.name} />
      <p className="eyebrow mb-6">{c.eyebrow}</p>
      <h1 data-testid="collection-title" className="font-display text-5xl uppercase leading-none md:text-8xl"><MaskLine delay={0.05}>{c.name}</MaskLine>{" "}<span data-testid="collection-title-sub" className="mt-4 block font-display text-2xl normal-case italic leading-tight text-[var(--gold-soft)] md:text-4xl">{c.h1Sub}</span></h1>
      <p data-testid="collection-intro" className="mt-6 max-w-2xl whitespace-pre-line text-sm leading-relaxed text-[var(--parchment)]/80 md:text-base">{intro}</p>
      {needs.length > 2 ? (
        <div data-testid="collection-filters" className="-mx-5 mt-12 flex gap-2 overflow-x-auto border-y border-[var(--line)] px-5 py-6 [scrollbar-width:none] md:mx-0 md:flex-wrap md:px-0">
          <Chip on={!need} onClick={() => setNeed("")} testid="collection-filter-all">Tous les besoins</Chip>
          {needs.map((n) => <Chip key={n} on={need === n} onClick={() => setNeed(n)} testid={`collection-filter-${n}`}>{n}</Chip>)}
        </div>
      ) : <div className="mt-12 border-t border-[var(--line)]" />}
      {isLoading && <p className="mt-10 text-[var(--muted)]">Chargement…</p>}
      {isError && <p className="mt-10 text-[var(--muted)]">La boutique est momentanément indisponible.</p>}
      {c.sections.map((s) => {
        const items = shown.filter(s.filter);
        if (!items.length) return null;
        return (
          <section key={s.id} data-testid={`collection-section-${s.id}`} className="mt-12">
            <h2 className="mb-8 flex items-baseline gap-3 font-display text-3xl italic">{s.title}<span className="font-sans text-sm not-italic text-[var(--muted)]">{items.length} soin{items.length > 1 ? "s" : ""}</span></h2>
            <div className={`grid grid-cols-1 gap-x-6 gap-y-14 ${c.cols}`}>
              {items.map((p, i) => <ProductCard key={p.slug} p={p} index={i} details />)}
            </div>
          </section>
        );
      })}
      {c.needs && list.length ? <Guide c={c} list={list} /> : null}
      <OtherRanges current={c.path} />
    </main>
  );
}
